<template>
  <div class="package-mobile-box">
    <div v-if="cover" class="package-cover">
      <img :src="cover" :alt="content.name" @error="handleImageError" />
    </div>
    <div v-else-if="thumbnail" class="package-thumbnail">
      <img :src="thumbnail" :alt="content.name" @error="handleImageError" />
    </div>
    <div v-else class="package-placeholder">
      <div class="placeholder-content">
        <i class="fas fa-image"></i>
        <p>No Image Available</p>
      </div>
    </div>
    <div class="package-content">
      <h2 class="package-title">{{ content.name }}</h2>
      <div class="package-price-section">
        <span v-if="content.price" class="package-price">
          {{ formatPrice(content.price) }} บาท
        </span>
        <span v-if="content.originalPrice && content.originalPrice !== content.price" class="package-original-price">
          {{ formatPrice(content.originalPrice) }} บาท
        </span>
      </div>
      <div class="package-badges">
        <div v-if="content.target" class="package-target">
          {{ getTargetText(content.target) }}
        </div>
        <div class="package-billing-cycle">
          <span v-if="content.billingCycle === 0" class="billing-cycle-badge one-time">
            {{ getBillingCycleText(content.billingCycle) }}
          </span>
          <span v-else class="billing-cycle-badge recurring">
            {{ getBillingCycleText(content.billingCycle) }}
          </span>
        </div>
        <div v-if="content.isSecret" class="package-secret">
          <span class="secret-badge">แพ็คลับ</span>
        </div>
      </div>
      <div v-if="content.detail" class="package-detail">
        <div v-safe-html="formatDetail(content.detail)"></div>
      </div>
      <div v-if="content.termsAndConditions" class="package-terms">
        <h4 class="terms-title">เงื่อนไขการใช้บริการ</h4>
        <div v-safe-html="formatDetail(content.termsAndConditions)" class="terms-content"></div>
      </div>
      <div v-if="content.projects && content.projects.length > 0" class="package-projects">
        <h4 class="projects-title">โครงการที่รองรับ</h4>
        <div class="projects-list">
          <span
            v-for="(project, index) in content.projects"
            :key="index"
            class="project-code"
          >
            {{ project.project_code }}
          </span>
        </div>
      </div>
      <div v-if="content.homes && content.homes.length > 0" class="package-homes">
        <h4 class="homes-title">บ้านที่รองรับ</h4>
        <div class="homes-list">
          <span
            v-for="(home, index) in content.homes"
            :key="index"
            class="home-item"
          >
            {{ getHomeDisplayText(home) }}
          </span>
        </div>
      </div>
      <div v-if="content.services && content.services.length > 0" class="package-services">
        <h4 class="services-title">บริการที่รวมอยู่</h4>
        <div class="services-list">
          <div
            v-for="(service, index) in content.services"
            :key="index"
            class="service-item"
          >
            {{ service.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatCurrencyAndTwoDecimalsString } from "@/lib/formatter";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

export default {
  name: "PackageMobileBox",
  props: {
    content: {
      type: Object,
      required: true
    },
    cover: {
      type: String,
      default: ""
    },
    thumbnail: {
      type: String,
      default: ""
    }
  },
  methods: {
    /**
     * Handle image loading errors
     */
    handleImageError(event) {
      const { target } = event;
      target.style.display = 'none';
    },

    /**
     * Format price with Thai number formatting
     */
    formatPrice(price) {
      return formatCurrencyAndTwoDecimalsString(price, 0);
    },

    /**
     * Get target text for display
     */
    getTargetText(target) {
      if (typeof target === 'object' && target !== null) {
        return target.name || '';
      }

      const targetMap = {
        "0": "สำหรับบ้าน",
        "1": "สำหรับคอนโด",
        "2": "สำหรับบ้านขนาดใหญ่"
      };

      return targetMap[target] || target;
    },

    /**
     * Get billing cycle text for display
     */
    getBillingCycleText(billingCycle) {
      if (typeof billingCycle !== 'number' || billingCycle < 0) {
        return "";
      }
      if (billingCycle === 0) {
        return "แพ็กเกจครั้งเดียว";
      }
      if (billingCycle === 1) {
        return "แพ็กเกจรายเดือน";
      }
      return `แพ็กเกจราย ${billingCycle} เดือน`;
    },

    /**
     * Get home display text with address and project code
     */
    getHomeDisplayText(home) {
      if (typeof home === 'string') {
        return home;
      }
      if (!home) {
        return 'Unknown';
      }

      const addressField = home.address_number || home.address_no;
      if (addressField && home.project_code) {
        return `${addressField} (${home.project_code})`;
      }

      const fallbackFields = ['address_number', 'address_no', 'project_code', 'house_no', 'project_name'];
      const match = fallbackFields.find((key) => home[key]);
      if (match) return home[match];

      return home.toString();
    },

    /**
     * Format detail content from JSON or plain text
     */
    formatDetail(detail) {
      if (!detail) return '';

      let html;
      // Try to parse as JSON (Quill editor format)
      try {
        const parsed = JSON.parse(detail);
        if (parsed.ops) {
          html = this.convertQuillToHtml(parsed.ops);
        } else {
          html = detail.replace(/\n/g, '<br>');
        }
      } catch (e) {
        // Not JSON, treat as plain text
        html = detail.replace(/\n/g, '<br>');
      }

      return sanitizeHtml(html);
    },

    /**
     * Convert Quill editor delta to HTML.
     * Preserves common inline attributes (bold, italic, underline, link).
     * Embedded objects (images, video) are rendered as a single space.
     * DOMPurify runs downstream — no HTML escaping needed here as long as
     * the output stays inside the sanitize pipeline.
     */
    convertQuillToHtml(delta) {
      const html = delta.reduce((acc, op) => {
        if (!op || op.insert == null) {
          return acc;
        }
        if (typeof op.insert !== 'string') {
          return `${acc} `;
        }

        const attrs = op.attributes || {};
        let openTags = '';
        let closeTags = '';
        if (attrs.link) {
          openTags += `<a href="${attrs.link}">`;
          closeTags = `</a>${closeTags}`;
        }
        if (attrs.bold) {
          openTags += '<strong>';
          closeTags = `</strong>${closeTags}`;
        }
        if (attrs.italic) {
          openTags += '<em>';
          closeTags = `</em>${closeTags}`;
        }
        if (attrs.underline) {
          openTags += '<u>';
          closeTags = `</u>${closeTags}`;
        }
        return `${acc}${openTags}${op.insert}${closeTags}`;
      }, '');

      return html.replace(/\n/g, '<br>');
    }
  }
};
</script>

<style scoped>
.package-mobile-box {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  margin: 0 auto;
}

.package-cover,
.package-thumbnail {
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
}

.package-cover img,
.package-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.package-placeholder {
  width: 100%;
  height: 200px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e9ecef;
}

.placeholder-content {
  text-align: center;
  color: #6c757d;
}

.placeholder-content i {
  font-size: 48px;
  margin-bottom: 8px;
  display: block;
}

.placeholder-content p {
  margin: 0;
  font-size: 14px;
}

.package-content {
  padding: 20px;
}

.package-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.package-price-section {
  margin-bottom: 8px;
}

.package-price {
  font-size: 24px;
  font-weight: 700;
  color: #e74c3c;
  margin-right: 8px;
}

.package-original-price {
  font-size: 18px;
  color: #999;
  text-decoration: line-through;
}

.package-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.package-target {
  font-size: 14px;
  color: #27ae60;
  font-weight: 500;
  padding: 4px 8px;
  background: #e8f5e8;
  border-radius: 4px;
  display: inline-block;
}

.package-billing-cycle {
  display: inline-block;
}

.billing-cycle-badge {
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.billing-cycle-badge.one-time {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.billing-cycle-badge.recurring {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.package-secret {
  display: inline-block;
}

.secret-badge {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.package-detail {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 16px;
}

.package-detail >>> br {
  margin-bottom: 8px;
}

.package-terms {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.terms-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.terms-content {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.terms-content >>> br {
  margin-bottom: 8px;
}

.package-projects {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.projects-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.projects-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.project-code {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #495057;
  font-family: monospace;
}

.package-homes {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.homes-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.homes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.home-item {
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  color: #0066cc;
  font-family: monospace;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.package-services {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.services-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-item {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  color: #1565c0;
  font-weight: 500;
}

@media screen and (max-width: 768px) {
  .package-mobile-box {
    border-radius: 0;
    box-shadow: none;
    max-width: 100%;
  }

  .package-cover,
  .package-thumbnail {
    height: 180px;
  }

  .package-content {
    padding: 16px;
  }

  .package-title {
    font-size: 18px;
  }

  .package-price {
    font-size: 20px;
  }
}
</style>
